import { useRef, useState } from 'react';

import { RefreshCw } from 'lucide-react';

type SidebarTag = { id: string; name: string; cardCount?: number };
export type CreateSidebarTagResult = 'created' | 'conflict' | 'error';

export function TagsSidebarList({
  tags,
  selectedTagId,
  onSelect,
  onCreate,
  onManage,
  totalTagCount,
  totalCardCount,
  loading,
  error,
  onRetry,
}: {
  tags: SidebarTag[];
  selectedTagId: string | null;
  onSelect: (tagId: string | null) => void;
  onCreate: (name: string) => Promise<CreateSidebarTagResult>;
  onManage: () => void;
  totalTagCount: number;
  totalCardCount: number;
  loading: boolean;
  error: string;
  onRetry: () => void;
}) {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const submittingRef = useRef(false);

  const cancelCreate = () => {
    if (submittingRef.current) return;
    setCreating(false);
    setDraft('');
    setCreateError('');
  };

  const submitCreate = async () => {
    if (submittingRef.current) return;

    const name = draft.trim();
    if (!name) {
      cancelCreate();
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setCreateError('');

    try {
      const result = await onCreate(name);
      if (result === 'created') {
        setCreating(false);
        setDraft('');
      } else {
        setCreateError(result === 'conflict' ? 'Tag already exists.' : 'Unable to create tag.');
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-col rounded-2xl border border-[#ece9e2] bg-white px-3 pb-3 pt-3.5 font-[family-name:var(--font-meaning-sans)]">
      <div className="flex items-baseline justify-between gap-2 px-[5px] pb-2.5">
        <div className="text-lg font-extrabold text-[#1e1c18]">Tags</div>
        <div className="text-[13px] text-[#a39c8d]">{loading ? '…' : totalTagCount}</div>
      </div>
      <div className="flex min-h-0 flex-col gap-px overflow-y-auto">
        {loading && tags.length === 0 && (
          <div role="status" className="px-[11px] py-2 text-sm text-[#a39c8d]">Loading tags…</div>
        )}
        {error && (
          <div role="alert" className="mb-1 flex items-center justify-between gap-2 rounded-[9px] bg-red-50 px-[11px] py-2 text-xs font-medium text-red-700">
            <span>{error}</span>
            <button type="button" onClick={onRetry} aria-label="Retry loading tags" className="rounded p-1 hover:bg-red-100">
              <RefreshCw size={14} aria-hidden="true" />
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={() => onSelect(null)}
          className={
            selectedTagId === null
              ? 'flex items-center justify-between gap-2 rounded-[9px] bg-[#2f5c4e] px-[11px] py-[7px] text-left text-[15px] font-bold text-white'
              : 'flex items-center justify-between gap-2 rounded-[9px] px-[11px] py-[7px] text-left text-[15px] font-semibold text-[#3d3a33] hover:bg-[#f6f4ef]'
          }
        >
          <span className="min-w-0 truncate">All words</span>
          <span className={selectedTagId === null ? 'shrink-0 font-semibold opacity-75' : 'shrink-0 font-semibold text-[#a39c8d]'}>
            {totalCardCount}
          </span>
        </button>
        {tags.map((tag) => {
          const selected = selectedTagId === tag.id;
          return (
            <button
              key={tag.id}
              type="button"
              onClick={() => onSelect(tag.id)}
              className={
                selected
                  ? 'flex items-center justify-between gap-2 rounded-[9px] bg-[#2f5c4e] px-[11px] py-[7px] text-left text-[15px] font-bold text-white'
                  : 'flex items-center justify-between gap-2 rounded-[9px] px-[11px] py-[7px] text-left text-[15px] font-semibold text-[#3d3a33] hover:bg-[#f6f4ef]'
              }
            >
              <span className="min-w-0 truncate">{tag.name}</span>
              <span className={selected ? 'shrink-0 font-semibold opacity-75' : 'shrink-0 font-semibold text-[#a39c8d]'}>
                {tag.cardCount ?? 0}
              </span>
            </button>
          );
        })}
      </div>
      <div className="mt-2 border-t border-[#f4f1ec] px-[5px] pt-2">
        {creating ? (
          <input
            autoFocus
            value={draft}
            maxLength={50}
            disabled={submitting}
            aria-label="New tag name"
            aria-invalid={Boolean(createError)}
            aria-describedby={createError ? 'new-tag-error' : undefined}
            placeholder={submitting ? 'Creating…' : 'Tag name'}
            onChange={(event) => {
              setDraft(event.target.value);
              setCreateError('');
            }}
            onBlur={() => void submitCreate()}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void submitCreate();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                cancelCreate();
              }
            }}
            className="h-8 w-full rounded-lg border border-[#d9d3c7] bg-white px-[7px] text-[14px] font-semibold text-[#3d3a33] outline-none transition placeholder:text-[#a39c8d] focus:border-[#2f5c4e] disabled:cursor-wait disabled:opacity-60"
          />
        ) : (
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                setCreating(true);
                setCreateError('');
              }}
              className="rounded-lg px-[6px] py-1 text-left text-[14px] font-bold text-[#2f5c4e] transition hover:bg-[#f6f4ef]"
            >
              + New tag
            </button>
            <button
              type="button"
              onClick={onManage}
              className="rounded-lg px-[6px] py-1 text-[14px] font-bold text-[#7b7669] transition hover:bg-[#f6f4ef] hover:text-[#3d3a33]"
            >
              Manage
            </button>
          </div>
        )}
        {createError && (
          <p id="new-tag-error" role="alert" className="px-[7px] pt-1.5 text-xs font-medium text-red-600">
            {createError}
          </p>
        )}
      </div>
    </div>
  );
}
